import { useSeo } from "@/hooks/useSeo";

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl sm:text-2xl text-ink">{title}</h2>
      <div className="mt-4 space-y-3 text-sm sm:text-base leading-relaxed text-stone-600">{children}</div>
    </section>
  );
}

function Placeholder({ children }) {
  return (
    <span className="bg-clay text-terracotta-dark font-semibold px-1.5 py-0.5 rounded">{children}</span>
  );
}

export default function PrivacyPolicy() {
  useSeo({
    title: "Privacy Policy | Brunch & Bottega",
    description: "Informativa privacy di Brunch & Bottega: quali dati raccogliamo, perché e quali diritti hai. Bozza tecnica da completare con i dati legali del titolare.",
    path: "/privacy-policy",
  });

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-28" data-testid="privacy-page">
      <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-5">Documento legale</p>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink">Privacy Policy</h1>
      <p className="mt-4 text-sm text-stone-500">Ultimo aggiornamento: <Placeholder>[DATA DI AGGIORNAMENTO]</Placeholder></p>

      <div className="mt-8 bg-clay/60 border border-terracotta/30 rounded-2xl p-5 text-sm leading-relaxed text-stone-700" data-testid="privacy-draft-notice">
        <strong className="text-ink">Nota importante.</strong> Questo documento è una bozza tecnica preparata
        in fase di sviluppo del sito. I campi evidenziati devono essere completati dal titolare dell'attività
        e il testo deve essere revisionato da un professionista legale prima della pubblicazione definitiva.
        Questo sito non dichiara né garantisce conformità al GDPR: l'obiettivo è raccogliere il minor numero
        di dati possibile ed essere trasparenti su come vengono trattati.
      </div>

      <Section title="1. Titolare del trattamento">
        <p>
          Il titolare del trattamento dei dati personali è: <Placeholder>[TITOLARE DEL TRATTAMENTO / RAGIONE SOCIALE]</Placeholder>,
          con sede in <Placeholder>[INDIRIZZO LEGALE]</Placeholder>, P.IVA <Placeholder>[P.IVA / CODICE FISCALE]</Placeholder>.
        </p>
        <p>
          Contatti del titolare: email <a href="mailto:brunchbottega@gmail.com" className="underline underline-offset-2 text-forest font-medium">brunchbottega@gmail.com</a> — telefono <a href="tel:+393392303993" className="underline underline-offset-2 text-forest font-medium">+39 339 230 3993</a>.
        </p>
      </Section>

      <Section title="2. Quali dati raccogliamo">
        <p>Questo sito raccoglie esclusivamente i dati che decidi di inviarci tramite i moduli di richiesta:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Modulo brunch / eventi:</strong> nome, email, telefono (facoltativo), data preferita, numero di persone, tipo di evento, messaggio.</li>
          <li><strong>Modulo contatti:</strong> nome, email, oggetto e messaggio.</li>
        </ul>
        <p>
          Non raccogliamo dati di navigazione a fini di profilazione, non usiamo strumenti di analytics,
          non installiamo pixel pubblicitari e non cediamo dati a terzi per finalità di marketing.
          I log tecnici del server (indirizzi IP, orari di accesso) sono gestiti dal fornitore di hosting:
          <Placeholder>[FORNITORE DI HOSTING — da verificare e documentare]</Placeholder>.
        </p>
      </Section>

      <Section title="3. Perché li raccogliamo (finalità e base giuridica)">
        <p>
          I dati inviati tramite i moduli vengono usati <strong>solo per rispondere alla tua richiesta</strong>
          (preventivi, disponibilità, informazioni sui prodotti). La base giuridica è l'esecuzione di misure
          precontrattuali su tua richiesta (art. 6, par. 1, lett. b GDPR) e, per l'invio del modulo,
          il tuo consenso espresso tramite l'apposita casella.
        </p>
        <p>
          Non inviamo newsletter né comunicazioni promozionali. Se in futuro verranno attivate,
          richiederanno un consenso separato e specifico.
        </p>
      </Section>

      <Section title="4. Dove vengono conservati i dati e per quanto tempo">
        <p>
          I dati dei moduli sono conservati su server situati in <Placeholder>[LOCALIZZAZIONE SERVER — da completare]</Placeholder>.
          Periodo di conservazione: <strong className="text-ink">12 mesi dalla richiesta</strong>,
          dopo i quali i dati vengono eliminati automaticamente dal sistema.
        </p>
      </Section>

      <Section title="5. A chi vengono comunicati">
        <p>
          I dati non vengono venduti né comunicati a terzi per finalità proprie. Possono essere trattati,
          in qualità di responsabili, dai fornitori tecnici che mantengono il sito:
          <Placeholder>[ELENCO FORNITORI TECNICI — hosting, manutenzione — da completare]</Placeholder>.
        </p>
      </Section>

      <Section title="6. Richieste verso servizi esterni">
        <p>
          Il sito è progettato per minimizzare le richieste verso terze parti: i caratteri tipografici
          e tutte le immagini sono ospitati direttamente su questo sito (self-hosted), senza chiamate
          a Google Fonts o a CDN fotografici esterni.
        </p>
        <p>Rimangono alcune chiamate tecniche generate dalla piattaforma che ospita il sito:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong>Cloudflare</strong> (rete di distribuzione e sicurezza della piattaforma di hosting):
            imposta due cookie tecnici anti-bot (cf_clearance, __cf_bm) e carica un piccolo script di
            misurazione delle visite senza cookie (Cloudflare Web Analytics). Dettagli nella Cookie Policy.
          </li>
          <li>
            <strong>Emergent</strong> (piattaforma di hosting): script tecnico caricato da assets.emergent.sh.
          </li>
        </ul>
        <p>
          Inoltre, quando invii un modulo, i dati inseriti vengono trasmessi ai fornitori che recapitano
          la notifica email al titolare: <Placeholder>[FORNITORE RECAPITO EMAIL — proxy della piattaforma / Resend]</Placeholder> e
          alla casella Gmail del titolare (Google).
        </p>
      </Section>

      <Section title="7. I tuoi diritti">
        <p>
          Ai sensi degli artt. 15–22 del GDPR puoi chiedere in qualsiasi momento: accesso ai tuoi dati,
          rettifica, cancellazione, limitazione del trattamento, opposizione e portabilità.
          Puoi esercitare i diritti scrivendo a <a href="mailto:brunchbottega@gmail.com" className="underline underline-offset-2 text-forest font-medium">brunchbottega@gmail.com</a>.
        </p>
        <p>
          Hai inoltre diritto di reclamo al Garante per la protezione dei dati personali
          (www.garanteprivacy.it).
        </p>
      </Section>

      <Section title="8. Modifiche a questa informativa">
        <p>
          Questa informativa può essere aggiornata nel tempo. La data di ultima modifica è indicata in cima
          alla pagina. Ti invitiamo a consultarla periodicamente.
        </p>
      </Section>
    </article>
  );
}
